/*
 * Kkumi bias / favourite-group typeahead.
 *
 * Three picker modes, one shared catalogue:
 *
 *   - Group multi (`.js-kkumi-group-multi-typeahead`)
 *     Multistans pick more than one favourite group. Sectioned dropdown
 *     (Popular → All groups) when the search box is empty; flat filtered
 *     list once they start typing. Each option carries a checkbox indicator
 *     and a generation tag ("4th gen"). Picking toggles the option and
 *     renders/removes a chip below the input. Strict — non-matching text
 *     never becomes a chip. (Used by the footer email-signup.)
 *
 *   - Group panel (`.js-kkumi-group-panel`)
 *     The richer favourite-groups picker (account profile): a chips field with
 *     a chevron that expands an inline panel — search box, an A–Z jump strip,
 *     ★ Popular quick row, and the full directory under sticky A–Z letter
 *     headings, with a Clear all / dot meter + "N / 8 picked" / Done footer.
 *     At the cap a note at the top of the list says how to swap one out.
 *     Collapsed by default ("Pick up to 8 groups" when empty). Each pill carries the
 *     same generation tag ("4th gen") as the dropdown's options. Shares the chip
 *     bag + KkumiGroups contract with the dropdown above, so serializers are
 *     unchanged.
 *
 *     Cards layout (add `js-kkg-cards` too): the chips field is replaced by
 *     a grid of square group cards (gen chip · name · ×) with an
 *     "Add a group" card and an Edit button that open the same panel.
 *
 *   - Group single (`.js-kkumi-group-typeahead`)
 *     Legacy single-pick group typeahead. Same suggestion catalogue, but
 *     no checkboxes and a single value lives in the input. Strict.
 *
 *   - Bias (`.js-kkumi-bias-typeahead`)
 *     Soft-strict member typeahead. Suggestions come from the flat union
 *     of every group's `members[]` (active or not — fan of a disbanded
 *     group still picks their old bias). Each suggestion renders as
 *     "Name · Group" with the group's generation tag. Picking writes
 *     "Name · Group" verbatim into the input so the disambiguation
 *     stays visible; the worker normaliser splits it back into
 *     bias + bias_group on the way through.
 *
 * Lazy-loaded: catalogue isn't fetched until a typeahead input gets focus,
 * then cached in sessionStorage for the life of the tab.
 *
 * Security (per FRONTEND.md):
 *   - Every API-controlled value rendered via textContent / DOM APIs.
 *   - The popover hangs off the existing .kkumi-input host so the
 *     floating-label `input + label` adjacency stays intact.
 */
(function () {
  var ENDPOINT = '/apps/rewards/bias-directory';
  var DEBOUNCE_MS = 80;
  var MAX_FILTERED = 50; // cap filtered results so the dropdown doesn't blow up on a 1-char query
  // Most favourite groups a member can pick. Mirrors the worker's
  // LIMITS.FAV_GROUPS_MAX (apps/rewards/src/lib/config.js) — keep the two in sync;
  // the worker truncates server-side too as a backstop.
  var MAX_GROUPS = 8;
  var DIRECTORY_PROMISE = null;

  // Editorial: the groups that surface in the "Popular" section when the
  // search box is empty. Order = display order. Anything not in this list
  // falls through to "All groups" (alpha). Matching is by aliasKey so casing
  // / punctuation differences in the directory don't drop a match.
  var POPULAR_GROUP_NAMES = [
    'BTS', 'BLACKPINK', 'SEVENTEEN', 'Stray Kids', 'NewJeans', 'TWICE',
    'aespa', 'ENHYPEN', 'LE SSERAFIM', 'IVE', 'ATEEZ', 'TOMORROW X TOGETHER',
    'NCT 127', 'ITZY', '(G)I-DLE', 'NMIXX',
  ];

  // Generation tag shown next to each group/member option. The value comes
  // from the live directory (admin Settings → Group directory → per-row
  // generation dropdown), surfaced via `/bias-directory`. Members inherit
  // their parent group's generation server-side. The group pickers also let
  // you SEARCH by it ("4th" / "4th gen" → every 4th-gen group). Pure UI/UX —
  // never persisted on the customer profile, never sent to Klaviyo.

  // Inline SVG for the checkbox checkmark. Loaded as a string and parsed
  // once so we can clone it per option (cheaper than re-parsing).
  var CHECK_SVG = '<svg viewBox="0 0 10 10" fill="none" stroke="#fff" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M2 5.2L4.2 7.5 8 2.5"/></svg>';
  // Vector × used on chip remove buttons + the bias clear button — the
  // Unicode × glyph subpixels badly at chip sizes.
  var CLOSE_SVG = '<svg viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true" focusable="false"><path d="M3 3l6 6M9 3l-6 6"/></svg>';
  // Magnifier (panel search box) + chevron (panel expand toggle).
  var SEARCH_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="7"></circle><path d="m21 21-4.35-4.35"></path></svg>';
  var CHEVRON_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="6 9 12 15 18 9"></polyline></svg>';

  // A–Z bucket letter for a group name — the first alphabetic char, uppercased
  // (so "(G)I-DLE" buckets under G, "NCT 127" under N). Names with no letter
  // fall under "#".
  function letterOf(name) {
    var m = String(name || '').match(/[a-z]/i);
    return m ? m[0].toUpperCase() : '#';
  }

  function loadDirectory() {
    if (DIRECTORY_PROMISE) return DIRECTORY_PROMISE;
    // /bias-directory is public and shared across surfaces — read it through the
    // shared client cache (window.KKRewards) instead of a bespoke sessionStorage
    // copy. Falls back to a direct fetch when the cache isn't present.
    var src = window.KKRewards
      ? window.KKRewards.get('/bias-directory', { scope: 'public' })
      : fetch(ENDPOINT, { headers: { accept: 'application/json' } })
          .then(function (r) { return r.ok ? r.json() : { groups: [], members: [] }; });
    DIRECTORY_PROMISE = src
      .then(function (data) {
        return {
          groups: (data && Array.isArray(data.groups)) ? data.groups : [],
          members: (data && Array.isArray(data.members)) ? data.members : [],
        };
      })
      .catch(function () { return { groups: [], members: [] }; });
    return DIRECTORY_PROMISE;
  }

  // Merchant copy for the panel + bias modes: the nearest ancestor carrying
  // [data-kkumi-picker-copy] (the account Details section) exposes its section
  // settings as data-c-fp-<key> attributes. Anything missing falls back to the
  // shipped wording, so the email-signup form (no copy host) is unchanged.
  // [tokens] are filled from `map`.
  function pcopy(input, key, fallback, map) {
    var host = input && input.closest ? input.closest('[data-kkumi-picker-copy]') : null;
    var k = 'cFp' + key.charAt(0).toUpperCase() + key.slice(1);
    var out = host && host.dataset[k] ? host.dataset[k] : fallback;
    if (map) Object.keys(map).forEach(function (t) { out = out.split('[' + t + ']').join(String(map[t])); });
    return out;
  }

  function aliasKey(s) {
    return String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  function debounce(fn, ms) {
    var t = null;
    return function () {
      var ctx = this; var args = arguments;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, ms);
    };
  }

  // Substring match against a single field (name OR name+group concatenated
  // for bias). Returns the match rank: -1 no match, 0 prefix, 1 infix.
  function matchRank(needle, hay) {
    if (!needle || !hay) return -1;
    var i = hay.indexOf(needle);
    if (i < 0) return -1;
    return i === 0 ? 0 : 1;
  }

  function rankList(list, query, fields) {
    if (!query) return list.slice();
    var q = aliasKey(query);
    if (!q) return list.slice();
    var prefix = [];
    var infix = [];
    for (var i = 0; i < list.length; i++) {
      var item = list[i];
      var best = -1;
      for (var j = 0; j < fields.length; j++) {
        var hay = aliasKey(fields[j](item));
        var r = matchRank(q, hay);
        if (r === 0) { best = 0; break; }
        if (r > 0 && best < 1) best = 1;
      }
      if (best === 0) prefix.push(item);
      else if (best === 1) infix.push(item);
    }
    return prefix.concat(infix).slice(0, MAX_FILTERED);
  }

  // ── Popover plumbing shared across modes ─────────────────────────────────
  function mountPopover(input) {
    var host = input.closest('.kkumi-input') || input.parentElement;
    if (!host) return null;
    host.classList.add('has-kkumi-typeahead');
    var popover = document.createElement('ul');
    popover.className = 'kkumi-typeahead__list';
    popover.setAttribute('role', 'listbox');
    popover.hidden = true;
    host.appendChild(popover);
    return popover;
  }

  function emptyStateLi(title, sub) {
    var li = document.createElement('li');
    li.className = 'kkumi-typeahead__empty';
    var b = document.createElement('b');
    b.textContent = title;
    li.appendChild(b);
    if (sub) {
      var span = document.createElement('span');
      span.textContent = sub;
      li.appendChild(span);
    }
    return li;
  }

  function sectionLi(label) {
    var li = document.createElement('li');
    li.className = 'kkumi-typeahead__section';
    li.textContent = label;
    return li;
  }

  // Render an option <li>. Checkbox indicator only renders when
  // `showCheckbox: true` (multi-pick modes). Generation tag only renders
  // when `showGeneration: true` — group pickers want it, bias doesn't
  // (the gen belongs to the group, not the member, and surfacing it in the
  // bias dropdown is visual noise that doesn't help disambiguate).
  function optionLi(opt, state, onPick) {
    var li = document.createElement('li');
    li.className = 'kkumi-typeahead__opt';
    if (state.selected) li.classList.add('is-selected');
    if (state.highlighted) li.classList.add('is-highlighted');
    if (state.disabled) li.classList.add('is-disabled');
    li.setAttribute('role', 'option');
    if (state.selected) li.setAttribute('aria-selected', 'true');
    if (state.disabled) li.setAttribute('aria-disabled', 'true');
    if (state.showCheckbox) {
      var check = document.createElement('span');
      check.className = 'kkumi-typeahead__check';
      check.innerHTML = CHECK_SVG;
      li.appendChild(check);
    }
    var label = document.createElement('span');
    label.className = 'kkumi-typeahead__name';
    label.textContent = opt.name;
    li.appendChild(label);
    if (opt.group) {
      // Muted group suffix on bias rows. No leading "·" — that separator
      // only belongs in the input value once the row is picked.
      var meta = document.createElement('span');
      meta.className = 'kkumi-typeahead__group-suffix';
      meta.textContent = opt.group;
      li.appendChild(meta);
    }
    if (state.showGeneration && opt.generation) {
      var genEl = document.createElement('span');
      genEl.className = 'kkumi-typeahead__meta';
      genEl.textContent = opt.generation + ' gen';
      li.appendChild(genEl);
    }
    li.addEventListener('mousedown', function (e) {
      e.preventDefault();
      if (state.disabled) return; // at the group cap — an unpicked row can't be added
      onPick(opt, li);
    });
    return li;
  }

  // ── Group multi-mode (chip-based, sectioned, checkboxes) ────────────────
  function chipBagFor(input) {
    var host = input.closest('.kkumi-input') || input.parentElement;
    if (!host) return null;
    // Bag lives INSIDE the host as the first child so chips flow with the
    // input in a flex-wrap layout (`.is-tag-host`). Older bag placements
    // (sibling-below) are migrated forward by moving the existing node in.
    var bag = host.querySelector('[data-kkumi-chip-bag-for="' + input.id + '"]');
    if (!bag) {
      bag = host.parentElement.querySelector('[data-kkumi-chip-bag-for="' + input.id + '"]');
    }
    if (!bag) {
      bag = document.createElement('div');
      bag.className = 'kkumi-chip-bag';
      bag.setAttribute('data-kkumi-chip-bag-for', input.id);
    }
    if (bag.parentElement !== host) host.insertBefore(bag, host.firstChild);
    host.classList.add('is-tag-host');
    return bag;
  }

  function refreshTagHostState(input, bag) {
    var host = input.closest('.kkumi-input');
    if (!host) return;
    host.classList.toggle('has-picks', bag.querySelector('[data-kkumi-chip]') !== null);
  }

  function chipNamesIn(bag) {
    return [].slice.call(bag.querySelectorAll('[data-kkumi-chip]'))
      .map(function (el) { return el.dataset.kkumiChip; });
  }

  function addChipIfNew(input, bag, name) {
    if (!name) return false;
    var existing = chipNamesIn(bag).map(aliasKey);
    if (existing.indexOf(aliasKey(name)) !== -1) return false;
    if (existing.length >= MAX_GROUPS) return false; // at the cap — can't add another

    var chip = document.createElement('span');
    chip.className = 'kkumi-chip';
    chip.dataset.kkumiChip = name;
    var label = document.createElement('span');
    label.className = 'kkumi-chip__label';
    label.textContent = name;
    chip.appendChild(label);
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'kkumi-chip__remove';
    btn.setAttribute('aria-label', 'Remove ' + name);
    btn.innerHTML = CLOSE_SVG;
    btn.addEventListener('click', function () {
      chip.remove();
      refreshTagHostState(input, bag);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
    chip.appendChild(btn);
    bag.appendChild(chip);
    refreshTagHostState(input, bag);
    input.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }

  function removeChipByName(bag, name) {
    var key = aliasKey(name);
    var nodes = bag.querySelectorAll('[data-kkumi-chip]');
    for (var i = 0; i < nodes.length; i++) {
      if (aliasKey(nodes[i].dataset.kkumiChip) === key) {
        nodes[i].remove();
        return true;
      }
    }
    return false;
  }

  function popularSplit(allGroups) {
    var popKeys = POPULAR_GROUP_NAMES.map(aliasKey);
    var byKey = {};
    allGroups.forEach(function (g) { byKey[aliasKey(g.name)] = g; });
    var popular = [];
    var seen = {};
    for (var i = 0; i < popKeys.length; i++) {
      var hit = byKey[popKeys[i]];
      if (hit && !seen[popKeys[i]]) {
        popular.push(hit);
        seen[popKeys[i]] = true;
      }
    }
    var all = allGroups.filter(function (g) { return !seen[aliasKey(g.name)]; })
      .slice()
      .sort(function (a, b) { return a.name.localeCompare(b.name); });
    return { popular: popular, all: all };
  }

  function attachGroupMulti(input) {
    if (!input || input.dataset.kkumiTypeaheadOn === '1') return;
    input.dataset.kkumiTypeaheadOn = '1';
    input.setAttribute('autocomplete', 'off');
    input.setAttribute('autocorrect', 'off');
    input.setAttribute('autocapitalize', 'off');
    input.setAttribute('spellcheck', 'false');
    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-autocomplete', 'list');
    var popover = mountPopover(input);
    if (!popover) return;
    var bag = chipBagFor(input);
    var visible = []; // current flat list of <li> data refs for keyboard nav
    var highlightedIdx = -1;

    function close() {
      popover.hidden = true;
      popover.replaceChildren();
      visible = [];
      highlightedIdx = -1;
    }

    function setHighlight(i) {
      var lis = popover.querySelectorAll('.kkumi-typeahead__opt');
      highlightedIdx = Math.max(-1, Math.min(lis.length - 1, i));
      lis.forEach(function (el, idx) {
        el.classList.toggle('is-highlighted', idx === highlightedIdx);
      });
    }

    function toggleGroup(group) {
      var picked = chipNamesIn(bag).map(aliasKey);
      var isPicked = picked.indexOf(aliasKey(group.name)) !== -1;
      // At the cap, an unpicked row is a no-op (you must remove one first).
      if (!isPicked && picked.length >= MAX_GROUPS) return;
      if (isPicked) {
        removeChipByName(bag, group.name);
      } else {
        addChipIfNew(input, bag, group.name);
      }
      input.value = '';
      // Re-render so the checkbox state updates immediately + next pick
      // doesn't re-render with the just-toggled state.
      rerender();
      input.focus();
    }

    function rerender() {
      loadDirectory().then(function (dir) {
        if (document.activeElement !== input) return;
        var groups = dir.groups || [];
        var pickedKeys = chipNamesIn(bag).map(aliasKey);
        // At the cap, unpicked rows are disabled (can't add a 9th) while the
        // picked rows stay toggleable so a member can still swap one out.
        var atCap = pickedKeys.length >= MAX_GROUPS;
        popover.replaceChildren();
        visible = [];
        function mkLi(g) {
          var selected = pickedKeys.indexOf(aliasKey(g.name)) !== -1;
          var li = optionLi(g, {
            selected: selected,
            highlighted: false,
            showCheckbox: true,
            showGeneration: true,
            disabled: atCap && !selected,
          }, function (opt) { toggleGroup(opt); });
          popover.appendChild(li);
          visible.push({ li: li, data: g });
        }
        var q = input.value.trim();
        if (q) {
          // Filter mode — flat list, no sections.
          var filtered = rankList(groups, q, [
            function (g) { return g.name; },
            // Search by generation too — "4th" (or "4th gen") matches every
            // 4th-gen group. Empty string for gen-less groups just never hits.
            function (g) { return g.generation ? g.generation + ' gen' : ''; },
            // …and by fandom name ("STAY" → Stray Kids).
            function (g) { return g.fandom || ''; },
          ]);
          if (!filtered.length) {
            popover.appendChild(emptyStateLi('No groups match “' + q + '”', 'Try a different spelling.'));
          } else {
            filtered.forEach(mkLi);
          }
        } else {
          // Sectioned mode — Popular + All.
          var split = popularSplit(groups);
          if (split.popular.length) {
            popover.appendChild(sectionLi('Popular'));
            split.popular.forEach(mkLi);
          }
          if (split.all.length) {
            popover.appendChild(sectionLi('All groups'));
            split.all.forEach(mkLi);
          }
          if (!visible.length) {
            popover.appendChild(emptyStateLi('No groups yet', 'Check back soon.'));
          }
        }
        // Pinned footer hint at the cap — sticks to the bottom of the dropdown
        // (shared `.kkumi-typeahead__hint` sticky styling) so it's always visible.
        if (atCap) {
          var capLi = document.createElement('li');
          capLi.className = 'kkumi-typeahead__hint';
          capLi.textContent = 'Max ' + MAX_GROUPS + ' groups. Remove one to add another.';
          popover.appendChild(capLi);
        }
        popover.hidden = false;
        // Auto-highlight the top match when the member is typing — so
        // Enter picks it without an extra ArrowDown. When the box is
        // empty (Popular / All sections showing), no auto-highlight.
        setHighlight(input.value.trim() ? 0 : -1);
      });
    }

    var updateDebounced = debounce(rerender, DEBOUNCE_MS);
    input.addEventListener('focus', rerender);
    input.addEventListener('input', updateDebounced);
    input.addEventListener('blur', function () {
      setTimeout(function () {
        input.value = '';
        close();
      }, 150);
    });
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Backspace' && !input.value && bag.lastElementChild) {
        bag.lastElementChild.remove();
        refreshTagHostState(input, bag);
        input.dispatchEvent(new Event('change', { bubbles: true }));
        rerender();
        return;
      }
      if (popover.hidden) return;
      if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight(highlightedIdx + 1); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight(highlightedIdx - 1); }
      else if (e.key === 'Enter') {
        if (highlightedIdx >= 0 && visible[highlightedIdx]) {
          e.preventDefault();
          toggleGroup(visible[highlightedIdx].data);
        }
      } else if (e.key === 'Escape') { input.value = ''; close(); }
    });
  }

  // ── Group panel-mode (chip field + expandable A–Z picker) ───────────────
  // The richer favourite-groups picker: a chips-in-a-box field with a chevron
  // that expands an inline panel — a search box ("Search all N groups…"), an
  // A–Z jump strip, a ★ Popular quick row, and the full directory under sticky
  // A–Z letter headings, with a Clear all / dot meter + "N / 8 picked" / Done
  // footer. Collapsed by default (just the chips, or a "Pick up to 8 groups"
  // prompt when empty), so it stays compact until opened. Shares the chip bag + KkumiGroups
  // contract with the legacy dropdown, so the form serializers are unchanged.
  function attachGroupPanel(input) {
    if (!input || input.dataset.kkumiTypeaheadOn === '1') return;
    input.dataset.kkumiTypeaheadOn = '1';
    input.setAttribute('autocomplete', 'off');

    var bag = chipBagFor(input);            // makes the host a chip tag-host
    var host = input.closest('.kkumi-input');
    if (!host || !bag) return;
    host.classList.add('kkg-host');
    // The raw <input> is now just a data anchor (id keys the chip bag; its
    // `change` event drives the form serializers + our pill sync). Typing
    // happens in the panel's search box, so park the field.
    input.setAttribute('readonly', 'readonly');
    input.setAttribute('tabindex', '-1');
    input.setAttribute('aria-hidden', 'true');

    var chev = document.createElement('button');
    chev.type = 'button';
    chev.className = 'kkg-chevron';
    chev.setAttribute('aria-label', pcopy(input, 'chooseAria', 'Choose favourite groups'));
    chev.setAttribute('aria-expanded', 'false');
    chev.innerHTML = CHEVRON_SVG;
    host.appendChild(chev);

    // Empty-field prompt — what the closed field says before anything's picked
    // (hidden by CSS once the host has `.has-picks`).
    var prompt = document.createElement('span');
    prompt.className = 'kkg-prompt';
    prompt.textContent = pcopy(input, 'empty', 'Pick up to [max] groups', { max: MAX_GROUPS });
    host.appendChild(prompt);

    // Wrap field + panel so the inline panel sits full-width under the field
    // (rather than as a sibling that could land in a grid cell).
    var wrap = document.createElement('div');
    wrap.className = 'kkg-wrap';
    host.parentElement.insertBefore(wrap, host);
    wrap.appendChild(host);
    var panel = document.createElement('div');
    panel.className = 'kkg-panel';
    panel.hidden = true;
    wrap.appendChild(panel);

    // Cards layout — the account Details tab shows picks as square cards in
    // their own block instead of chips crammed into a field.
    var cardsMode = input.classList.contains('js-kkg-cards');
    var cardsEl = null, cardsGrid = null, cardsCount = null, editBtn = null, opener = null;
    var genByKey = {}, hydrated = false;
    if (cardsMode) {
      host.classList.add('kkg-host--cards');
      cardsEl = document.createElement('div');
      cardsEl.className = 'kkg-cards';
      var head = document.createElement('div');
      head.className = 'kkg-cards__head';
      var lab = host.querySelector('.kkumi-input__label');
      var t = document.createElement('p');
      t.className = 'kkg-cards__t';
      t.textContent = lab ? lab.textContent.trim() : '';
      cardsCount = document.createElement('span');
      cardsCount.className = 'kkg-cards__n';
      editBtn = document.createElement('button');
      editBtn.type = 'button';
      editBtn.className = 'kkg-cards__edit';
      editBtn.textContent = pcopy(input, 'edit', 'Edit');
      editBtn.setAttribute('aria-expanded', 'false');
      editBtn.setAttribute('aria-label', pcopy(input, 'chooseAria', 'Choose favourite groups'));
      editBtn.addEventListener('click', function () { opener = editBtn; togglePanel(); });
      head.appendChild(t); head.appendChild(cardsCount); head.appendChild(editBtn);
      cardsGrid = document.createElement('ul');
      cardsGrid.className = 'kkg-cards__grid';
      cardsGrid.setAttribute('role', 'list');
      cardsEl.appendChild(head);
      cardsEl.appendChild(cardsGrid);
      wrap.insertBefore(cardsEl, panel);
    }

    var built = false, searchInput = null, countEl = null, emptyEl = null, clearBtn = null;
    var meterEl = null, capNote = null, azEl = null, scrollEl = null, flashT = null, nopeEl = null;
    var popSec = null, allSec = null;
    // Touch devices: don't auto-focus the search on open — the keyboard would
    // pop up and cover the list they came to browse.
    var canHover = !!(window.matchMedia && window.matchMedia('(hover: hover)').matches);

    function pill(g) {
      var name = g.name;
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'kkg-pill';
      b.dataset.kkgPill = name;
      // Stash the generation so the panel search can match on it too ("4th" →
      // every 4th-gen group), not just the name.
      if (g.generation) b.dataset.kkgGen = g.generation;
      if (g.fandom) b.dataset.kkgFandom = g.fandom;
      genByKey[aliasKey(name)] = g.generation || '';
      b.setAttribute('aria-pressed', 'false');
      var ic = document.createElement('span');
      ic.className = 'kkg-pill__check';
      ic.innerHTML = CHECK_SVG;
      b.appendChild(ic);
      var lab = document.createElement('span');
      lab.className = 'kkg-pill__label';
      lab.textContent = name;
      b.appendChild(lab);
      // Generation tag ("4th gen") — same live directory value the multi
      // dropdown surfaces via optionLi's showGeneration. Omitted when the group
      // has no generation set. Pure UI/UX — never persisted / never to Klaviyo.
      if (g.generation) {
        var gen = document.createElement('span');
        gen.className = 'kkg-pill__gen';
        gen.textContent = g.generation + ' gen';
        b.appendChild(gen);
      }
      b.addEventListener('click', function () { togglePill(name, b); });
      return b;
    }

    function build(dir) {
      var groups = (dir && dir.groups) || [];
      panel.replaceChildren();

      var search = document.createElement('div');
      search.className = 'kkg-search';
      search.innerHTML = SEARCH_SVG;
      searchInput = document.createElement('input');
      searchInput.type = 'text';
      searchInput.className = 'kkg-search__input';
      searchInput.placeholder = pcopy(input, 'search', 'Search all [n] groups…', { n: groups.length });
      searchInput.setAttribute('autocomplete', 'off');
      searchInput.addEventListener('input', function () { filter(searchInput.value); });
      search.appendChild(searchInput);
      panel.appendChild(search);

      // A–Z jump strip — one button per letter that has groups; scrolls the
      // list to that letter. Hidden while searching (the list is filtered).
      azEl = document.createElement('div');
      azEl.className = 'kkg-az';
      panel.appendChild(azEl);

      var scroll = document.createElement('div');
      scroll.className = 'kkg-scroll';
      scrollEl = scroll;
      panel.appendChild(scroll);

      // At-the-cap note — sits at the top of the list once all MAX_GROUPS are
      // picked, so the dimmed pills below are explained up front (not only
      // after a member taps one). Pressing a dimmed pill flashes it.
      capNote = document.createElement('p');
      capNote.className = 'kkg-cap';
      capNote.setAttribute('role', 'status');
      capNote.hidden = true;
      capNote.textContent = pcopy(input, 'limit', "That's the max of [max]. Tap a picked group to swap it out.", { max: MAX_GROUPS });
      scroll.appendChild(capNote);

      // ★ Popular quick row (a shortcut subset; the same groups also appear in
      // A–Z below).
      var split = popularSplit(groups);
      if (split.popular.length) {
        popSec = document.createElement('div');
        popSec.className = 'kkg-sec kkg-sec--popular';
        var ph = document.createElement('div'); ph.className = 'kkg-sec__head'; ph.textContent = pcopy(input, 'popular', '★ Popular');
        popSec.appendChild(ph);
        var pp = document.createElement('div'); pp.className = 'kkg-pills';
        split.popular.forEach(function (g) { pp.appendChild(pill(g)); });
        popSec.appendChild(pp);
        scroll.appendChild(popSec);
      }

      // All groups · A–Z — every group, bucketed under its first letter.
      allSec = document.createElement('div');
      allSec.className = 'kkg-sec kkg-sec--all';
      var ah = document.createElement('div'); ah.className = 'kkg-sec__head'; ah.textContent = pcopy(input, 'all', 'All groups · A–Z');
      allSec.appendChild(ah);
      var buckets = {}, order = [];
      groups.slice().sort(function (a, b) { return a.name.localeCompare(b.name); }).forEach(function (g) {
        var L = letterOf(g.name);
        if (!buckets[L]) { buckets[L] = []; order.push(L); }
        buckets[L].push(g);
      });
      order.sort(function (a, b) { if (a === '#') return 1; if (b === '#') return -1; return a.localeCompare(b); });
      order.forEach(function (L) {
        var letEl = document.createElement('div'); letEl.className = 'kkg-letter';
        var lh = document.createElement('div'); lh.className = 'kkg-letter__head'; lh.textContent = L;
        letEl.appendChild(lh);
        var jb = document.createElement('button');
        jb.type = 'button'; jb.className = 'kkg-az__btn'; jb.textContent = L;
        jb.setAttribute('aria-label', pcopy(input, 'jumpAria', 'Jump to [l]', { l: L }));
        jb.addEventListener('click', function () {
          scroll.scrollTop = letEl.offsetTop; // .kkg-scroll is position:relative
        });
        azEl.appendChild(jb);
        var lp = document.createElement('div'); lp.className = 'kkg-pills';
        buckets[L].forEach(function (g) { lp.appendChild(pill(g)); });
        letEl.appendChild(lp);
        allSec.appendChild(letEl);
      });
      scroll.appendChild(allSec);

      emptyEl = document.createElement('div');
      emptyEl.className = 'kkg-empty';
      emptyEl.hidden = true;
      scroll.appendChild(emptyEl);

      // Footer: Clear all · a dot meter + "[n] / [max] picked" · Done.
      var foot = document.createElement('div'); foot.className = 'kkg-foot';
      clearBtn = document.createElement('button'); clearBtn.type = 'button'; clearBtn.className = 'kkg-clear'; clearBtn.textContent = pcopy(input, 'clear', 'Clear all');
      clearBtn.addEventListener('click', clearAll);
      var prog = document.createElement('div'); prog.className = 'kkg-prog';
      meterEl = document.createElement('span'); meterEl.className = 'kkg-meter'; meterEl.setAttribute('aria-hidden', 'true');
      for (var d = 0; d < MAX_GROUPS; d++) meterEl.appendChild(document.createElement('i'));
      countEl = document.createElement('span'); countEl.className = 'kkg-count'; countEl.setAttribute('role', 'status'); countEl.setAttribute('aria-live', 'polite');
      prog.appendChild(meterEl); prog.appendChild(countEl);
      var done = document.createElement('button'); done.type = 'button'; done.className = 'button button--small kkg-done'; done.textContent = pcopy(input, 'done', 'Done');
      done.addEventListener('click', function () { closePanel(); returnFocus(); });
      foot.appendChild(clearBtn); foot.appendChild(prog); foot.appendChild(done);
      panel.appendChild(foot);

      built = true;
      syncPills();
      renderCards(); // generation tags are known now
    }

    // ── Cards layout ──
    // Each card is a plain square with the group name as the hero.
    var NAME_LINES = 3, NAME_MIN_PX = 10;
    // Shrink each name until it fits its card: no word wider than the card
    // (CSS never breaks inside a word — "SEVENTEEN" stays whole) and no more
    // than NAME_LINES lines. Starts from the CSS size, so short names stay
    // big; anything that still won't fit at the floor may break mid-word.
    function fitNames() {
      // Not laid out yet (hidden) — the ResizeObserver fits once it shows.
      if (!cardsGrid || !cardsGrid.clientWidth) return;
      cardsGrid.querySelectorAll('.kkg-card__name').forEach(function (el) {
        el.style.fontSize = '';
        el.classList.remove('is-fit', 'is-squeezed');
        el.classList.add('is-fitting'); // unclamped while measuring
        var cs = window.getComputedStyle(el);
        var size = parseFloat(cs.fontSize) || 16;
        var lh = (parseFloat(cs.lineHeight) || size * 1.15) / size;
        function fits() {
          return el.scrollWidth <= el.clientWidth + 0.5 && el.scrollHeight <= size * lh * NAME_LINES + 1;
        }
        while (!fits() && size > NAME_MIN_PX) {
          size -= 0.5;
          el.style.fontSize = size + 'px';
        }
        el.classList.remove('is-fitting');
        el.classList.add(fits() ? 'is-fit' : 'is-squeezed');
      });
    }
    if (cardsMode) {
      if (window.ResizeObserver) {
        var lastW = 0;
        new ResizeObserver(function (entries) {
          var w = Math.round(entries[0].contentRect.width);
          if (w && w !== lastW) { lastW = w; fitNames(); }
        }).observe(cardsGrid);
      }
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(fitNames);
    }
    function renderCards() {
      if (!cardsMode) return;
      var names = chipNamesIn(bag);
      var loading = input.disabled && !hydrated;
      cardsEl.classList.toggle('is-loading', loading);
      cardsCount.textContent = loading ? '' : pcopy(input, 'count', '[n] / [max] picked', { n: names.length, max: MAX_GROUPS });
      cardsGrid.replaceChildren();
      if (loading) {
        for (var k = 0; k < 4; k++) {
          var ghost = document.createElement('li');
          ghost.className = 'kkg-card kkg-card--ghost';
          if (window.KKSkel) ghost.appendChild(window.KKSkel.el({ fill: true }));
          cardsGrid.appendChild(ghost);
        }
        return;
      }
      names.forEach(function (name) {
        var li = document.createElement('li');
        li.className = 'kkg-card';
        li.title = name; // full name on hover if a long one is clamped
        var gen = genByKey[aliasKey(name)];
        if (gen) {
          var g = document.createElement('span');
          g.className = 'kkg-card__gen';
          g.textContent = gen + ' gen';
          li.appendChild(g);
        }
        var nm = document.createElement('span');
        nm.className = 'kkg-card__name';
        nm.textContent = name;
        li.appendChild(nm);
        var x = document.createElement('button');
        x.type = 'button';
        x.className = 'kkg-card__x';
        x.setAttribute('aria-label', pcopy(input, 'removeAria', 'Remove [name]', { name: name }));
        x.innerHTML = CLOSE_SVG;
        x.addEventListener('click', function () {
          // Keep keyboard focus on the grid: the next card's ×, else Add / Edit.
          var idx = names.indexOf(name);
          removeChipByName(bag, name);
          refreshTagHostState(input, bag);
          input.dispatchEvent(new Event('change', { bubbles: true })); // → renderCards
          var xs = cardsGrid.querySelectorAll('.kkg-card__x');
          var next = xs[Math.min(idx, xs.length - 1)] || cardsGrid.querySelector('.kkg-card--add') || editBtn;
          try { next.focus(); } catch (e) { /* noop */ }
        });
        li.appendChild(x);
        cardsGrid.appendChild(li);
      });
      if (names.length < MAX_GROUPS) {
        var addLi = document.createElement('li');
        addLi.className = 'kkg-card kkg-card--add';
        var add = document.createElement('button');
        add.type = 'button';
        add.className = 'kkg-card__add';
        add.setAttribute('aria-expanded', panel.hidden ? 'false' : 'true');
        var plus = document.createElement('span');
        plus.className = 'kkg-card__plus';
        plus.setAttribute('aria-hidden', 'true');
        plus.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><path d="M12 6v12M6 12h12"/></svg>';
        var al = document.createElement('span');
        al.className = 'kkg-card__addl';
        al.textContent = pcopy(input, 'add', 'Add a group');
        add.appendChild(plus);
        add.appendChild(al);
        if (!names.length) {
          var sub = document.createElement('span');
          sub.className = 'kkg-card__sub';
          sub.textContent = pcopy(input, 'empty', 'Pick up to [max] groups', { max: MAX_GROUPS });
          add.appendChild(sub);
        }
        add.addEventListener('click', function () { opener = add; if (panel.hidden) openPanel(); else closePanel(); });
        addLi.appendChild(add);
        cardsGrid.appendChild(addLi);
      }
      fitNames();
    }
    function returnFocus() {
      var to = cardsMode ? (opener && opener.isConnected ? opener : (cardsGrid.querySelector('.kkg-card__add') || editBtn)) : chev;
      try { to.focus(); } catch (e) { /* noop */ }
    }

    // Repaint every pill's selected / disabled state + the counter from the
    // chips (the source of truth). Driven by the input's `change` event so chip
    // × removals and KkumiGroups.setAll hydration both re-sync.
    function syncPills() {
      if (!built) return;
      var pickedKeys = chipNamesIn(bag).map(aliasKey);
      var atCap = pickedKeys.length >= MAX_GROUPS;
      panel.querySelectorAll('.kkg-pill').forEach(function (b) {
        var on = pickedKeys.indexOf(aliasKey(b.dataset.kkgPill)) !== -1;
        b.classList.toggle('is-on', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
        // At the cap an unpicked pill looks disabled but stays clickable, so
        // tapping it can say why (the inline limit message in the footer).
        var dis = atCap && !on;
        b.classList.toggle('is-disabled', dis);
        if (dis) b.setAttribute('aria-disabled', 'true'); else b.removeAttribute('aria-disabled');
      });
      if (countEl) {
        // "[n] / [max] picked" with the count in bold — built with DOM nodes.
        countEl.replaceChildren();
        var tpl = pcopy(input, 'count', '[n] / [max] picked', { max: MAX_GROUPS });
        tpl.split('[n]').forEach(function (part, i) {
          if (i) { var nb = document.createElement('b'); nb.textContent = String(pickedKeys.length); countEl.appendChild(nb); }
          if (part) countEl.appendChild(document.createTextNode(part));
        });
      }
      if (meterEl) {
        [].forEach.call(meterEl.children, function (dot, i) { dot.classList.toggle('is-on', i < pickedKeys.length); });
      }
      panel.classList.toggle('is-full', atCap);
      if (capNote) capNote.hidden = !atCap;
      if (clearBtn) clearBtn.disabled = !pickedKeys.length;
      refreshTagHostState(input, bag);
    }

    // At the cap, pressing an unpicked pill flashes the cap note (and nudges the
    // pill) instead of adding — the note says how to swap one out.
    function flashCap(b) {
      if (!capNote) return;
      capNote.classList.remove('is-flash');
      if (nopeEl) nopeEl.classList.remove('is-nope');
      void capNote.offsetWidth; // restart the animation
      capNote.classList.add('is-flash');
      nopeEl = b || null;
      if (nopeEl) nopeEl.classList.add('is-nope');
      clearTimeout(flashT);
      flashT = setTimeout(function () {
        capNote.classList.remove('is-flash');
        if (nopeEl) nopeEl.classList.remove('is-nope');
        nopeEl = null;
      }, 700);
      // Keep the note in view when the member has scrolled down the list.
      if (scrollEl && scrollEl.scrollTop > capNote.offsetTop) scrollEl.scrollTop = capNote.offsetTop;
    }

    function togglePill(name, b) {
      var picked = chipNamesIn(bag).map(aliasKey);
      var isPicked = picked.indexOf(aliasKey(name)) !== -1;
      if (!isPicked && picked.length >= MAX_GROUPS) { flashCap(b); return; } // at the cap — say so
      // Picking from search results: hand focus back to the search box with
      // the query selected, so the next name can be typed straight over it.
      if (searchInput && searchInput.value && canHover) {
        setTimeout(function () { try { searchInput.focus(); searchInput.select(); } catch (e) { /* noop */ } }, 0);
      }
      if (isPicked) {
        removeChipByName(bag, name);
        refreshTagHostState(input, bag);
        input.dispatchEvent(new Event('change', { bubbles: true })); // → syncPills
      } else {
        addChipIfNew(input, bag, name);                              // dispatches change
      }
    }

    function clearAll() {
      bag.replaceChildren();
      refreshTagHostState(input, bag);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // Search filter — hide the Popular shortcut while searching (A–Z carries
    // every group, so results aren't duplicated), filter A–Z pills, and collapse
    // any letter block with no matches. Shows an empty state when nothing hits.
    // Matches on the group NAME, its GENERATION — typing "4th" (or "4th gen")
    // surfaces every 4th-gen group — or its FANDOM name ("STAY"). Each is
    // checked separately so a term can't accidentally match across a boundary.
    function filter(q) {
      if (!built) return;
      var query = aliasKey(q);
      if (popSec) popSec.hidden = !!query;
      if (azEl) azEl.hidden = !!query;
      var any = false;
      allSec.querySelectorAll('.kkg-letter').forEach(function (letEl) {
        var anyInLetter = false;
        letEl.querySelectorAll('.kkg-pill').forEach(function (b) {
          var genKey = b.dataset.kkgGen ? aliasKey(b.dataset.kkgGen + ' gen') : '';
          var fanKey = b.dataset.kkgFandom ? aliasKey(b.dataset.kkgFandom) : '';
          var match = !query
            || aliasKey(b.dataset.kkgPill).indexOf(query) !== -1
            || (genKey && genKey.indexOf(query) !== -1)
            || (fanKey && fanKey.indexOf(query) !== -1);
          b.hidden = !match;
          if (match) anyInLetter = true;
        });
        letEl.hidden = !anyInLetter;
        if (anyInLetter) any = true;
      });
      if (emptyEl) {
        emptyEl.hidden = any;
        emptyEl.textContent = any ? '' : pcopy(input, 'none', 'No groups match “[q]”', { q: q });
      }
    }

    function openPanel() {
      if (input.disabled) return; // still ghost-loading from /profile — not ready
      panel.hidden = false;
      host.classList.add('is-open');
      chev.setAttribute('aria-expanded', 'true');
      if (cardsMode) {
        editBtn.setAttribute('aria-expanded', 'true');
        var addB = cardsGrid.querySelector('.kkg-card__add');
        if (addB) addB.setAttribute('aria-expanded', 'true');
        cardsEl.classList.add('is-open');
      }
      syncPills();
      if (scrollEl) scrollEl.scrollTop = 0;
      if (searchInput && canHover) { try { searchInput.focus({ preventScroll: true }); } catch (e) { /* noop */ } }
      // Bring the panel into view (it opens inline, below the fold on phones).
      try { panel.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); } catch (e) { /* noop */ }
    }
    function closePanel() {
      panel.hidden = true;
      host.classList.remove('is-open');
      chev.setAttribute('aria-expanded', 'false');
      if (cardsMode) {
        editBtn.setAttribute('aria-expanded', 'false');
        var addB = cardsGrid.querySelector('.kkg-card__add');
        if (addB) addB.setAttribute('aria-expanded', 'false');
        cardsEl.classList.remove('is-open');
      }
      if (searchInput) { searchInput.value = ''; filter(''); }
    }
    function togglePanel() { if (panel.hidden) openPanel(); else closePanel(); }

    // Clicking the field opens the panel (but not when hitting a chip × or the
    // chevron, which have their own handlers).
    host.addEventListener('click', function (e) {
      if (e.target.closest('.kkumi-chip__remove') || e.target.closest('.kkg-chevron')) return;
      if (panel.hidden) openPanel();
    });
    chev.addEventListener('click', function (e) { e.stopPropagation(); togglePanel(); });
    document.addEventListener('mousedown', function (e) {
      if (panel.hidden) return;
      if (!wrap.contains(e.target)) closePanel();
    });
    document.addEventListener('keydown', function (e) {
      if (!panel.hidden && e.key === 'Escape') { closePanel(); if (wrap.contains(document.activeElement) || document.activeElement === document.body) returnFocus(); }
    });
    // Any chip mutation (pill toggle, chip ×, hydration) re-syncs the pills
    // (and the cards). The first one is the /profile hydration.
    input.addEventListener('change', function () { hydrated = true; syncPills(); renderCards(); });
    renderCards();

    loadDirectory().then(build);
  }

  // ── Group single-mode (strict, legacy) ──────────────────────────────────
  function findExactByName(list, value) {
    var v = (value || '').trim();
    if (!v) return null;
    for (var i = 0; i < list.length; i++) if (list[i].name === v) return list[i];
    return null;
  }

  function attachGroupSingle(input) {
    if (!input || input.dataset.kkumiTypeaheadOn === '1') return;
    input.dataset.kkumiTypeaheadOn = '1';
    input.setAttribute('autocomplete', 'off');
    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-autocomplete', 'list');
    var popover = mountPopover(input);
    if (!popover) return;
    var visible = [];
    var highlightedIdx = -1;

    function close() {
      popover.hidden = true;
      popover.replaceChildren();
      visible = [];
      highlightedIdx = -1;
    }
    function setHighlight(i) {
      var lis = popover.querySelectorAll('.kkumi-typeahead__opt');
      highlightedIdx = Math.max(-1, Math.min(lis.length - 1, i));
      lis.forEach(function (el, idx) { el.classList.toggle('is-highlighted', idx === highlightedIdx); });
    }
    function pick(opt) {
      input.value = opt.name;
      close();
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
    function rerender() {
      loadDirectory().then(function (dir) {
        if (document.activeElement !== input) return;
        var groups = dir.groups || [];
        var filtered = rankList(groups, input.value, [
          function (g) { return g.name; },
          function (g) { return g.generation ? g.generation + ' gen' : ''; },
          function (g) { return g.fandom || ''; },
        ]);
        popover.replaceChildren();
        visible = [];
        if (!filtered.length) {
          popover.appendChild(emptyStateLi('No groups match', ''));
        } else {
          filtered.forEach(function (g) {
            var li = optionLi(g, { selected: false, highlighted: false, showCheckbox: false, showGeneration: true }, pick);
            popover.appendChild(li);
            visible.push({ li: li, data: g });
          });
        }
        popover.hidden = false;
        // Auto-highlight the top match when the member is typing — so
        // Enter picks it without an extra ArrowDown. When the box is
        // empty (Popular / All sections showing), no auto-highlight.
        setHighlight(input.value.trim() ? 0 : -1);
      });
    }
    input.addEventListener('focus', rerender);
    input.addEventListener('input', debounce(rerender, DEBOUNCE_MS));
    input.addEventListener('blur', function () {
      setTimeout(function () {
        loadDirectory().then(function (dir) {
          var groups = dir.groups || [];
          if (input.value && !findExactByName(groups, input.value)) {
            input.value = '';
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
          close();
        });
      }, 150);
    });
    input.addEventListener('keydown', function (e) {
      if (popover.hidden) return;
      if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight(highlightedIdx + 1); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight(highlightedIdx - 1); }
      else if (e.key === 'Enter') {
        if (highlightedIdx >= 0 && visible[highlightedIdx]) {
          e.preventDefault();
          pick(visible[highlightedIdx].data);
        }
      } else if (e.key === 'Escape') { close(); }
    });
  }

  // ── Bias mode (soft-strict, "Name · Group" in the input) ────────────────
  // After a row is picked we want the input to *show* "A-na · Hearts2Hearts"
  // with the group portion greyed, but a real <input> can only paint its
  // value in a single colour. The pick keeps the canonical "Name · Group"
  // value on the input (form serialization stays correct), then overlays a
  // styled <span> that re-renders the value with mixed colour. The input
  // itself goes transparent-text + transparent-caret while the overlay is
  // up. Typing / clicking back into the input drops the overlay.
  function attachBias(input) {
    if (!input || input.dataset.kkumiTypeaheadOn === '1') return;
    input.dataset.kkumiTypeaheadOn = '1';
    input.setAttribute('autocomplete', 'off');
    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-autocomplete', 'list');
    var popover = mountPopover(input);
    if (!popover) return;
    var host = input.closest('.kkumi-input') || input.parentElement;
    var visible = [];
    var highlightedIdx = -1;

    // Overlay: shows the picked value with mixed styling. Lives inside the
    // host so its positioning sits on top of the input. The clear button
    // also lives on the overlay for visual coherence.
    var overlay = document.createElement('span');
    overlay.className = 'kkumi-bias-picked';
    overlay.hidden = true;
    overlay.setAttribute('aria-hidden', 'true');
    var overlayName = document.createElement('span');
    overlayName.className = 'kkumi-bias-picked__name';
    var overlayGroup = document.createElement('span');
    overlayGroup.className = 'kkumi-bias-picked__group';
    overlay.appendChild(overlayName);
    overlay.appendChild(overlayGroup);
    host.appendChild(overlay);

    var clearBtn = document.createElement('button');
    clearBtn.type = 'button';
    clearBtn.className = 'kkumi-bias-clear';
    clearBtn.setAttribute('aria-label', pcopy(input, 'biasClearAria', 'Clear ult bias'));
    clearBtn.innerHTML = CLOSE_SVG;
    clearBtn.hidden = true;
    host.appendChild(clearBtn);

    function showPicked(name, group) {
      overlayName.textContent = name;
      overlayGroup.textContent = group ? '  ·  ' + group : '';
      overlay.hidden = false;
      clearBtn.hidden = false;
      host.classList.add('is-bias-picked');
    }
    function hidePicked() {
      overlay.hidden = true;
      clearBtn.hidden = true;
      host.classList.remove('is-bias-picked');
    }
    function syncPickedFromInputValue() {
      var v = (input.value || '').trim();
      var sep = v.indexOf(' · ');
      if (sep > 0) {
        showPicked(v.slice(0, sep).trim(), v.slice(sep + 3).trim());
      } else if (v) {
        showPicked(v, null);
      } else {
        hidePicked();
      }
    }

    function close() {
      popover.hidden = true;
      popover.replaceChildren();
      visible = [];
      highlightedIdx = -1;
    }
    function setHighlight(i) {
      var lis = popover.querySelectorAll('.kkumi-typeahead__opt');
      highlightedIdx = Math.max(-1, Math.min(lis.length - 1, i));
      lis.forEach(function (el, idx) { el.classList.toggle('is-highlighted', idx === highlightedIdx); });
    }
    function pick(opt) {
      input.value = opt.group ? (opt.name + ' · ' + opt.group) : opt.name;
      previousValue = '';            // new pick replaces the stashed one
      syncPickedFromInputValue();
      close();
      input.blur();
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
    function rerender() {
      loadDirectory().then(function (dir) {
        if (document.activeElement !== input) return;
        var members = dir.members || [];
        var filtered = rankList(members, input.value, [
          function (m) { return m.name; },
          function (m) { return (m.name || '') + ' ' + (m.group || ''); },
        ]);
        popover.replaceChildren();
        visible = [];
        var isFiltering = !!input.value.trim();
        var slice = isFiltering ? filtered : filtered.slice(0, 30);
        if (!slice.length) {
          popover.appendChild(emptyStateLi(pcopy(input, 'biasNone', 'No matches'), pcopy(input, 'biasNoneSub', 'Try a member name.')));
        } else {
          slice.forEach(function (m) {
            var li = optionLi(m, { selected: false, highlighted: false, showCheckbox: false, showGeneration: false }, pick);
            popover.appendChild(li);
            visible.push({ li: li, data: m });
          });
          // When showing the unfiltered preview (first 30 members), surface a
          // hint so members know more results are available as they type — the
          // directory is lazy-loaded and large, so the subset can look sparse.
          if (!isFiltering) {
            var hintLi = document.createElement('li');
            hintLi.className = 'kkumi-typeahead__hint';
            hintLi.textContent = pcopy(input, 'biasMore', 'Type for more results…');
            popover.appendChild(hintLi);
          }
        }
        popover.hidden = false;
        // Auto-highlight the top match when the member is typing — so
        // Enter picks it without an extra ArrowDown. When the box is
        // empty (Popular / All sections showing), no auto-highlight.
        setHighlight(input.value.trim() ? 0 : -1);
      });
    }

    // Stash the previous picked value when the member focuses the field,
    // so a blur-without-picking restores it (rather than dropping their
    // bias because they tabbed away). Cleared when they actually pick
    // something new or click ×.
    var previousValue = '';

    clearBtn.addEventListener('click', function () {
      input.value = '';
      previousValue = '';
      hidePicked();
      input.dispatchEvent(new Event('change', { bubbles: true }));
      input.focus();
    });
    input.addEventListener('focus', function () {
      // Clicking back into a picked field empties the input rather than
      // exposing the raw "Name · Group" string for editing — that string
      // includes the separator + group suffix which the member would
      // have to backspace one char at a time. Empty input + dropdown
      // means "pick a new bias"; blur without picking puts the old one
      // back.
      previousValue = input.value;
      input.value = '';
      hidePicked();
      rerender();
    });
    // One debounced handler — building a fresh debounce() per keystroke (as
    // before) reset its timer every time, so it never actually debounced.
    var debouncedRerender = debounce(rerender, DEBOUNCE_MS);
    input.addEventListener('input', function () {
      hidePicked();
      debouncedRerender();
    });
    input.addEventListener('blur', function () {
      setTimeout(function () {
        close();
        // No new value typed/picked → restore the prior pick. Anything
        // typed (even partial) stays on the input so the server-side
        // soft-strict normaliser can still resolve it.
        if (!input.value.trim() && previousValue) {
          input.value = previousValue;
        }
        previousValue = '';
        syncPickedFromInputValue();
      }, 150);
    });
    input.addEventListener('keydown', function (e) {
      if (popover.hidden) return;
      if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight(highlightedIdx + 1); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight(highlightedIdx - 1); }
      else if (e.key === 'Enter') {
        if (highlightedIdx >= 0 && visible[highlightedIdx]) {
          e.preventDefault();
          pick(visible[highlightedIdx].data);
        }
      } else if (e.key === 'Escape') { close(); }
    });
    // Show the overlay on initial mount if the input already has a value
    // (account-details hydrates from /profile asynchronously, so we also
    // listen for kkumi:bias:rehydrate to re-sync once the value lands).
    syncPickedFromInputValue();
    input.addEventListener('kkumi:bias:rehydrate', syncPickedFromInputValue);
  }

  // Public chip API used by signup + account-details form serializers.
  window.KkumiGroups = {
    read: function (input) {
      var bag = input && input.id ? document.querySelector('[data-kkumi-chip-bag-for="' + input.id + '"]') : null;
      return bag ? chipNamesIn(bag) : [];
    },
    setAll: function (input, names) {
      var bag = chipBagFor(input);
      if (!bag) return;
      bag.replaceChildren();
      (Array.isArray(names) ? names : []).forEach(function (n) {
        if (n) addChipIfNew(input, bag, n);
      });
      refreshTagHostState(input, bag);
    },
  };

  function scan(root) {
    var r = root || document;
    r.querySelectorAll('input.js-kkumi-group-multi-typeahead, input[data-kkumi-group-multi-typeahead]')
      .forEach(attachGroupMulti);
    r.querySelectorAll('input.js-kkumi-group-panel, input[data-kkumi-group-panel]')
      .forEach(attachGroupPanel);
    r.querySelectorAll('input.js-kkumi-group-typeahead, input[data-kkumi-group-typeahead]')
      .forEach(attachGroupSingle);
    r.querySelectorAll('input.js-kkumi-bias-typeahead, input[data-kkumi-bias-typeahead]')
      .forEach(attachBias);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { scan(); });
  } else {
    scan();
  }
  document.addEventListener('kkumi:typeahead:rescan', function (e) { scan(e.target || document); });
})();
